import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, of, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class PollingService {
  constructor(private http: HttpClient) {}

  /**
   * Start polling API based on config
   * Returns an observable that emits success/failure result
   */

  startPolling(
    config: any,
    apiUrl: string,
    formId?: string,
  ): Observable<{ status: number; data: any }> {
    if (!apiUrl) {
      return of({
        status: 400,
        data: {
          success: false,
          message: 'Polling URL missing',
        },
      });
    }

    const maxAttempts = config.maxAttempts || 10;
    const intervalMs = config.intervalMs || 5000;

    const token = localStorage.getItem('accessToken') || '';
    let finalUrl = apiUrl
      .replace('{{api_endpoint}}', environment.api_endpoint)
      .replace('{{form_id}}', formId || '')
      .replace('{{token}}', token);

    const method = config.apiMethod?.toUpperCase() || 'GET';
    let body = config.body || {};

    if (
      formId === '69fc66de5965342c7808339e' ||
      formId == '6a00496b709ef6b5947f3fe5' ||
      formId == '69ff7970b97f4370a3018efe' ||
      formId == '6a04b1dc47fee7be767d9e49'
    ) {
      body = null;
    }

    const headersObj = Object.entries(config.headers || {}).reduce(
      (acc: any, [key, value]: [string, any]) => {
        acc[key] =
          typeof value === 'string' ? value.replace('{{token}}', token) : value;
        return acc;
      },
      {},
    );

    // Ensure product header and authorization are present and use typed HttpHeaders
    const accessToken = localStorage.getItem('accessToken') || '';
    const finalHeaders = {
      ...headersObj,
      // 'X-Product-frontend': 'cc_frontend',
    } as any;

    if (accessToken) {
      finalHeaders['Authorization'] = `Bearer ${accessToken}`;
    }

    const httpHeaders = new HttpHeaders(finalHeaders);

    const getValueByPath = (source: any, path: string) =>
      path.split('.').reduce((value: any, key: string) => value?.[key], source);

    const isApiConditionMatched = (apiResult: {
      status: number;
      data: any;
    }) => {
      const apiConditionGroups = config?.condition
        ?.map((group: any) => ({
          ...group,
          conditions: group.conditions?.filter(
            (cond: any) => cond.type === 'api',
          ),
        }))
        .filter((group: any) => group.conditions?.length);

      if (!apiConditionGroups?.length) {
        return false;
      }

      return apiConditionGroups.every((group: any) =>
        group.conditions.every((cond: any) => {
          const apiValue =
            cond.value === 'status'
              ? apiResult.status
              : getValueByPath(
                  { status: apiResult.status, data: apiResult.data },
                  cond.value,
                );

          return cond.equals?.some((eq: any) => eq === apiValue);
        }),
      );
    };

    if (method === 'GET' || method === 'HEAD') {
      if (body && Object.keys(body).length) {
        const queryParams: string[] = [];
        Object.entries(body).forEach(([key, value]) => {
          let paramValue: any = value;
          if (
            typeof value === 'string' &&
            value.startsWith('{{') &&
            value.endsWith('}}')
          ) {
            const storageKey = value.slice(2, -2);
            paramValue = localStorage.getItem(storageKey);
            if (paramValue === 'true' || paramValue == true) paramValue = true;
            else if (paramValue === 'false' || paramValue == false)
              paramValue = false;
          }
          queryParams.push(
            `${encodeURIComponent(key)}=${encodeURIComponent(paramValue)}`,
          );
        });
        const separator = finalUrl.includes('?') ? '&' : '?';
        finalUrl += `${separator}${queryParams.join('&')}`;
      }
      body = null;
    }

    return new Observable<{ status: number; data: any }>((subscriber) => {
      let attempts = 0;
      let timeoutId: any = null;
      let isStopped = false;

      const poll = () => {
        if (isStopped || attempts >= maxAttempts) {
          subscriber.complete();
          return;
        }

        attempts++;

        this.http
          .request(method, finalUrl, {
            headers: httpHeaders,
            body,
            observe: 'response',
          })
          .pipe(
            catchError((err) =>
              of({
                status: err.status || 500,
                data: err.error || {},
              }),
            ),
          )
          .subscribe((response: any) => {
            if (isStopped) return;

            const apiResult = {
              status: response.status ?? (response.body?.success ? 200 : 400),
              data: response.body || response.data || {},
            };

            const rawStatus =
              apiResult?.data?.data?.status ||
              apiResult?.data?.status ||
              apiResult?.data?.data?.data?.status ||
              apiResult?.data?.data?.integrationStatus ||
              apiResult?.data?.integrationStatus ||
              apiResult?.data?.data?.connectionStatus ||
              apiResult?.data?.connectionStatus ||
              apiResult?.data?.data?.state ||
              apiResult?.data?.state;

            const normalizedStatus = rawStatus
              ? String(rawStatus).toLowerCase().replace(/[-_]/g, ' ').trim()
              : '';

            const isInProgress =
              normalizedStatus === 'in progress' ||
              normalizedStatus === 'inprogress' ||
              normalizedStatus === 'pending' ||
              normalizedStatus === 'connecting';

            const isFailed =
              normalizedStatus === 'failed' || normalizedStatus === 'error';

            const isSuccess =
              normalizedStatus === 'success' ||
              normalizedStatus === 'completed' ||
              normalizedStatus === 'connected';

            const hasConditions =
              config?.condition && config.condition.length > 0;
            const apiConditionMatched = isApiConditionMatched(apiResult);

            if (
              isSuccess ||
              (apiConditionMatched && !isInProgress && !isFailed)
            ) {
              subscriber.next(apiResult);
              subscriber.complete();
              return;
            }

            if (
              (formId === '687f21113da59426ab18a54a' ||
                formId === '690deff281121521d33bd02e' ||
                formId === '6a04b1dc47fee7be767d9e49') &&
              apiResult.status === 200 &&
              (apiResult.data?.success === true ||
                apiResult.data?.success === 'true')
            ) {
              subscriber.next(apiResult);
              subscriber.complete();
              return;
            }

            if (attempts >= maxAttempts) {
              subscriber.next(apiResult);
              subscriber.complete();
              return;
            }

            subscriber.next(apiResult);
            timeoutId = setTimeout(poll, intervalMs);
          });
      };
      poll();
      return () => {
        isStopped = true;
        if (timeoutId) clearTimeout(timeoutId);
      };
    });
  }

  private stop$ = new Subject<void>();
  stopPolling() {
    this.stop$.next();
  }
}
